// Aura tab — standalone Aura Score flow. User uploads/takes a photo, we
// run the full editorial aura audit (1 credit) and surface the result.
// Lives at /app/aura.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, X, Sparkles, Wand2, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edge-error";
import { compressImage, uploadToBucket, signedUrl } from "@/lib/storage";
import { CreditsChip } from "@/components/CreditsChip";
import { BuyCreditsDialog } from "@/components/BuyCreditsDialog";
import { useCredits } from "@/hooks/useCredits";
import { AuraScoreRing } from "@/components/AuraScoreRing";
import { GlossyCard, glossyButtonClass } from "@/components/GlossyCard";
import { cn } from "@/lib/utils";
import { SEO } from "@/components/SEO";
import { useIsMobile } from "@/hooks/use-mobile";
import { AuraHomeDesktop } from "./aura/AuraHomeDesktop";
import { MobileShell } from "@/components/mobile";
import { CustomizeOutfitDialog, type CustomizeData } from "@/components/CustomizeOutfitDialog";
import { GenerationOverlay } from "@/components/GenerationOverlay";
import { useFreeTierGate } from "@/contexts/FreeTierGateContext";
import { setPendingGeneration, clearPendingGeneration, waitForTryonComplete } from "@/hooks/usePendingGeneration";

const SCAN_STAGES = [
  "Reading silhouette…",
  "Mapping proportions…",
  "Analyzing color season…",
  "Gauging statement factor…",
  "Checking photo-readiness…",
  "Composing your aura…",
];

const SUB_LABELS: Array<[string, string]> = [
  ["aura_silhouette", "Silhouette"],
  ["aura_proportions", "Proportions"],
  ["aura_color_season", "Color season"],
  ["aura_statement", "Statement factor"],
  ["aura_photo_readiness", "Photo-readiness"],
];

const STYLE_STAGES = [
  "Reading your photo…",
  "Picking the palette…",
  "Sketching the silhouette…",
  "Tailoring the look…",
  "Styling accessories…",
  "Polishing the final shot…",
];

interface AuraResult {
  tryonId: string;
  aura_score: number;
  aura_silhouette: number;
  aura_proportions: number;
  aura_color_season: number;
  aura_statement: number;
  aura_photo_readiness: number;
  aura_verdict: string;
  aura_tips: string[];
}

const AuraHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh: refreshCredits, credits } = useCredits();
  const isMobile = useIsMobile();
  const { runGate } = useFreeTierGate();

  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [stage, setStage] = useState(0);
  const [result, setResult] = useState<AuraResult | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [styling, setStyling] = useState(false);
  const [styleStage, setStyleStage] = useState(0);

  const cameraRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);

  // SEO handled by <SEO /> component below.

  // Cycle scanning labels
  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(() => setStage((s) => (s + 1) % SCAN_STAGES.length), 1100);
    return () => clearInterval(t);
  }, [scanning]);

  // Cycle styling labels (for the full-screen overlay)
  useEffect(() => {
    if (!styling) return;
    const t = setInterval(() => setStyleStage((s) => (s + 1) % STYLE_STAGES.length), 1400);
    return () => clearInterval(t);
  }, [styling]);

  const onPickPhoto = async (file?: File) => {
    if (!file) return;
    try {
      // Higher quality so Aura analysis and the displayed photo stay sharp.
      const compressed = await compressImage(file, 2.5, 2048);
      setPhoto(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
      setResult(null);
    } catch (e) {
      toast({ title: "Couldn't read image", description: String(e), variant: "destructive" });
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setResult(null);
  };

  const runAura = async () => {
    if (!user || !photo) return;
    // Free-tier ad-gate (Android only). Aura also counts toward the lifetime 3.
    const allowed = await runGate();
    if (!allowed) return;
    setScanning(true);
    setStage(0);
    try {
      const path = await uploadToBucket("user-photos", user.id, photo, "jpg");
      const { data, error } = await supabase.functions.invoke("aura-from-photo", {
        body: { photoPath: path },
      });
      if (error) {
        const ctx = (error as { context?: { status?: number } }).context;
        if (ctx?.status === 402) {
          setBuyOpen(true);
          return;
        }
        if (ctx?.status === 429) {
          toast({ title: "Rate limited", description: "Try again in a moment.", variant: "destructive" });
          return;
        }
        throw error;
      }
      setResult(data as AuraResult);
      await refreshCredits();
      toast({ title: "Aura ready ✨" });
    } catch (e) {
      const { message } = await readEdgeError(e);
      toast({
        title: "Couldn't read aura",
        description: message,
        variant: "destructive",
      });
    } finally {
      setScanning(false);
    }
  };

  // Style a full outfit on the uploaded photo (re-uses generate-outfit pipeline).
  const styleOutfit = async (data: CustomizeData) => {
    if (!user || !photo) return;
    if ((credits ?? 0) < 1) {
      setBuyOpen(true);
      return;
    }
    // Free-tier ad-gate (Android only). 1st free, 2nd & 3rd require ad, 4th = paywall.
    const allowed = await runGate();
    if (!allowed) return;
    setStyling(true);
    let pendingId: string | null = null;
    try {
      const userPath = await uploadToBucket("user-photos", user.id, photo, "jpg");
      const userUrl = await signedUrl("user-photos", userPath);

      const { data: pending, error: pendErr } = await supabase
        .from("tryons").insert({
          user_id: user.id,
          user_photo_url: userPath,
          status: "processing",
          kind: "outfit_only",
          product_category: "custom",
          product_style: "Aura · Custom outfit",
        }).select("id").single();
      if (pendErr) throw pendErr;
      pendingId = pending.id;
      setPendingGeneration({ id: pendingId, kind: "outfit", startedAt: Date.now() });

      const referenceImageUrls: string[] = [];
      for (const f of data.referenceFiles ?? []) {
        const p = await uploadToBucket("product-images", user.id, f, "jpg");
        referenceImageUrls.push(await signedUrl("product-images", p));
      }
      const validLinks = (data.referenceLinks ?? [])
        .map((l) => l?.trim())
        .filter((l): l is string => !!l && /^https?:\/\//i.test(l));
      for (const link of validLinks) {
        try {
          const { data: ext } = await supabase.functions.invoke("extract-product", { body: { url: link } });
          if (ext?.imageUrl) referenceImageUrls.push(ext.imageUrl);
        } catch { /* skip */ }
      }

      const { data: profile } = await supabase
        .from("profiles").select("*").eq("user_id", user.id).maybeSingle();

      const invokePromise = supabase.functions.invoke("generate-outfit", {
        body: {
          userPhotoUrl: userUrl,
          baseProductImageUrl: null,
          baseProduct: null,
          profile,
          customPrompt: data.prompt || undefined,
          referenceImageUrls: referenceImageUrls.length ? referenceImageUrls : undefined,
          tryonId: pendingId,
        },
      });

      const [terminal, invoke] = await Promise.all([
        waitForTryonComplete(pendingId),
        invokePromise.catch((e) => ({ data: null, error: e })),
      ]);

      if (invoke.error) {
        const info = await readEdgeError(invoke.error);
        if (info.status === 402) { await refreshCredits(); setBuyOpen(true); clearPendingGeneration(); return; }
        if (info.status === 429) {
          toast({ title: "Rate limited", description: "Try again in a moment.", variant: "destructive" });
          clearPendingGeneration();
          return;
        }
        if (terminal?.status !== "complete") throw new Error(info.message);
      }
      if (terminal?.status === "failed") throw new Error("Generation failed");
      if (!terminal) throw new Error("Generation timed out");

      clearPendingGeneration();
      await refreshCredits();
      navigate(`/app/result/${pendingId}`);
    } catch (e) {
      if (pendingId) {
        await supabase.from("tryons").update({ status: "failed" })
          .eq("id", pendingId).eq("user_id", user.id);
      }
      clearPendingGeneration();
      const { message } = await readEdgeError(e);
      toast({ title: "Couldn't style outfit", description: message, variant: "destructive" });
    } finally {
      setStyling(false);
    }
  };


  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const avatarUrl =
    (typeof meta.avatar_url === "string" && meta.avatar_url) ||
    (typeof meta.picture === "string" && meta.picture) ||
    "";
  const initials = (user?.email ?? "?").split(/\s+|@/)[0].slice(0, 2).toUpperCase();

  // Desktop redesign — Stitch "Aura Analysis" reference. Mobile keeps current UI.
  if (!isMobile) {
    return (
      <>
        <SEO />
        <AuraHomeDesktop
          photoPreview={photoPreview}
          scanning={scanning}
          scanLabel={SCAN_STAGES[stage]}
          result={result}
          onPickCamera={() => cameraRef.current?.click()}
          onPickUpload={() => uploadRef.current?.click()}
          onClearPhoto={clearPhoto}
          onRunAura={runAura}
          onNavHistory={() => navigate("/app/history")}
          avatarUrl={avatarUrl}
          initials={initials}
          onBuyCredits={() => setBuyOpen(true)}
        />
        {/* Hidden file inputs (shared with mobile path) */}
        <input
          ref={cameraRef}
          type="file"
          name="auraPhotoCamera"
          aria-label="Take aura photo"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => onPickPhoto(e.target.files?.[0])}
        />
        <input
          ref={uploadRef}
          type="file"
          name="auraPhotoUpload"
          aria-label="Upload aura photo"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPickPhoto(e.target.files?.[0])}
        />
        <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
      </>
    );
  }

  return (
    <>
      <SEO />
    <MobileShell title="Aura Score" actions={<CreditsChip onClick={() => setBuyOpen(true)} />}>
    <div className="mx-auto w-full max-w-md pt-4 pb-2 animate-fade-in">
      <p className="mb-5 text-sm text-muted-foreground">
        A deeper, editorial read on your <span className="font-semibold text-foreground">vibe, silhouette and signature style</span>.
        One photo · 0.10 credits for Aura Read.
      </p>

      {/* Info card — visible BEFORE upload so users know the alt path. */}
      {!result && !photoPreview && (
        <div className="mb-4 animate-fade-in rounded-3xl border border-primary/20 bg-gradient-to-br from-accent/10 to-transparent p-4 shadow-soft">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/25 to-accent/15 text-primary ring-1 ring-primary/20">
              <Wand2 className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">Style a full outfit too</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                After you add a photo you can also build a complete look — pick a template, drop product links or reference photos, or describe the vibe. Your face stays exactly the same.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo card with scanning overlay — glossy */}
      <GlossyCard
        tone="primary"
        shimmer={!scanning}
        className={cn(
          "border-primary/20 shadow-elevated !p-0",
          !photoPreview && "border-dashed",
        )}
      >
        <div className="relative aspect-[3/4] w-full bg-muted/40">
          {photoPreview ? (
            <>
              <img
                src={photoPreview}
                alt="Your photo"
                className={cn(
                  "h-full w-full object-cover transition-all duration-500",
                  scanning && "scale-[1.02]",
                )}
              />
              {/* Glossy highlight overlay */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/15"
              />
              {!scanning && !result && (
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-background/80 text-foreground shadow-soft backdrop-blur-md transition-all hover:scale-110 hover:bg-background"
                  aria-label="Remove photo"
                >
                  <X className="h-4 w-4" />
                </button>
              )}

              {/* Scanning overlay */}
              {scanning && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-primary/10" />
                  <div className="aura-scanline pointer-events-none absolute inset-x-0 h-24" />
                  <div className="pointer-events-none absolute inset-3">
                    <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-primary" />
                    <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-primary" />
                    <span className="absolute bottom-0 left-0 h-5 w-5 border-b-2 border-l-2 border-primary" />
                    <span className="absolute bottom-0 right-0 h-5 w-5 border-b-2 border-r-2 border-primary" />
                  </div>
                  <div
                    className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay"
                    style={{
                      backgroundImage:
                        "linear-gradient(to right, hsl(var(--primary)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--primary)) 1px, transparent 1px)",
                      backgroundSize: "32px 32px",
                    }}
                  />
                  <div className="absolute inset-x-4 bottom-4 flex items-center justify-center">
                    <div className="flex items-center gap-2 rounded-full border border-primary/30 bg-background/80 px-4 py-2 text-xs font-semibold text-foreground backdrop-blur-md">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                      {SCAN_STAGES[stage]}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-5 p-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/25 to-accent/15 text-primary shadow-soft ring-1 ring-primary/20 animate-scale-in">
                <Sparkles className="h-7 w-7" />
              </div>
              <div className="animate-fade-in">
                <p className="text-sm font-bold">Add your photo</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  A clean, full-body shot works best.
                </p>
              </div>
              <div className="grid w-full max-w-xs grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className={cn("h-11", glossyButtonClass)}
                  onClick={() => cameraRef.current?.click()}
                >
                  <Camera className="mr-2 h-4 w-4" />
                  Take photo
                </Button>
                <Button
                  type="button"
                  className={cn("h-11 gradient-primary text-primary-foreground", glossyButtonClass)}
                  onClick={() => uploadRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </Button>
              </div>
            </div>
          )}
        </div>
      </GlossyCard>

      {/* Hidden inputs — split so phones offer both camera + gallery. */}
      <input
        ref={cameraRef}
        type="file"
        name="auraPhotoCamera"
        aria-label="Take aura photo"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onPickPhoto(e.target.files?.[0])}
      />
      <input
        ref={uploadRef}
        type="file"
        name="auraPhotoUpload"
        aria-label="Upload aura photo"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPickPhoto(e.target.files?.[0])}
      />

      {/* Action buttons (after a photo is picked) */}
      {!result && photoPreview && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className={cn("h-12", glossyButtonClass)}
              onClick={() => uploadRef.current?.click()}
              disabled={scanning}
            >
              <Upload className="mr-2 h-4 w-4" />
              Change
            </Button>
            <Button
              className={cn("h-12 gradient-primary text-primary-foreground", glossyButtonClass)}
              onClick={runAura}
              disabled={!photo || scanning}
            >
              {scanning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Reading…
                </>
              ) : (
                <>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Read aura
                </>
              )}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={scanning}
            className="mt-2 flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          >
            <Camera className="h-3.5 w-3.5" />
            Retake with camera
          </button>
        </>
      )}

      {/* Style a full outfit on this photo — alternative path to reading aura */}
      {!result && photoPreview && (
        <div className="mt-5 animate-fade-in rounded-3xl border border-primary/20 bg-gradient-to-br from-accent/10 to-transparent p-4 shadow-soft">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/25 to-accent/15 text-primary ring-1 ring-primary/20">
              <Wand2 className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">Style a full outfit</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Use this photo to build a complete look — pick a template, add reference links or photos, or describe the vibe. Your face stays exactly the same.
              </p>
              <Button
                type="button"
                onClick={() => setCustomizeOpen(true)}
                disabled={styling || scanning}
                className={cn("mt-3 h-11 w-full gradient-primary text-primary-foreground", glossyButtonClass)}
              >
                {styling ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Styling…</>
                ) : (
                  <><Sparkles className="mr-2 h-4 w-4" /> Customize your outfit</>
                )}
              </Button>
              <p className="mt-2 text-center text-[11px] text-muted-foreground">
                1 credit · Style Score after is free · Refunded if it fails
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="mt-6 animate-fade-in rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 shadow-soft">
          <div className="flex flex-col items-center text-center">
            <AuraScoreRing score={result.aura_score} size={140} />
            {result.aura_verdict && (
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-foreground/90">
                "{result.aura_verdict}"
              </p>
            )}
          </div>

          <div className="mt-6 space-y-2.5">
            {SUB_LABELS.map(([key, label]) => {
              const v = (result as unknown as Record<string, number>)[key];
              if (typeof v !== "number") return null;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-semibold tabular-nums">{v}</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary to-primary/60 transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {Array.isArray(result.aura_tips) && result.aura_tips.length > 0 && (
            <div className="mt-6">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Aura Tips
              </p>
              <ul className="space-y-2">
                {result.aura_tips.map((t, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-11" onClick={clearPhoto}>
              New aura
            </Button>
            <Button
              className="h-11"
              onClick={() => navigate("/app/history")}
            >
              View history
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />
      <CustomizeOutfitDialog
        busy={styling}
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
        hideTrigger
        onGenerate={styleOutfit}
      />
    </div>
    </MobileShell>
    <GenerationOverlay open={styling} stage={STYLE_STAGES[styleStage]} />
    </>
  );
};

export default AuraHome;
